"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { cn } from "@utils/utils";
import { getMultiToneGradientColor } from "@helpers/getMultiToneGradientColor";
import { getPath } from "@helpers/urlHelper";
import { getColorSlug } from "@helpers/getColorSlug";

import { useGlobalLoader } from "@hooks/useGlobalLoader";
import assetLoader from "@utils/internalImageLoder";
import styles from "./ColorSwitchButton.module.scss";
import ImageAtom from "@components/atoms/Image";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";

interface ColorButtonProps {
  color: {
    color: string[];
    name: string;
    type?: string;
  };
  isActive: boolean;
  type?: string;
  isVariant?: boolean;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  pageType?: string;
  isSellable?: boolean;
  carAvailability?: string;
  colorButtonClassname?: string;
}

export default function ColorButton({
  color,
  isActive,
  type,
  isVariant = false,
  brandName,
  modelName,
  variantName,
  pageType,
  isSellable,
  carAvailability,
  colorButtonClassname = "",
}: ColorButtonProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { showLoader, hideLoader } = useGlobalLoader();
  const colorCodes = Array.isArray(color?.color) ? color.color : [];
  const isDualTone = color.type === "DT" || colorCodes.length > 1;
  const { name: cityName } = getSelectedCityClient();

  useEffect(() => {
    hideLoader();
  }, [pathname, isActive, hideLoader]);

  const buildColorPath = () => {

    if (isVariant) {
      const nextSearchParams = new URLSearchParams(searchParams?.toString());
      nextSearchParams.set("color", getColorSlug(color?.name));
      const query = nextSearchParams.toString();
      return query ? `${pathname || "/"}?${query}` : pathname || "/";
    }

    return getPath(pathname || "/", color, isVariant);
  };

  const clickHandler = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    showLoader(`Loading ${color.name} color`);

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || "",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_color",
        car_availability: carAvailability || "",
        vehicle_make: brandName || "",
        vehicle_model: modelName || "",
        vehicle_variant: variantName || "",
        city_name: cityName || "",
        colour: color?.name || "",
      },
    });

    router.push(buildColorPath());
  };

  return (
    <>
      <a
        href={buildColorPath()}
        className={cn(
          styles["colors__hero-available-colors-list-item-button"],
          colorButtonClassname,
          isDualTone &&
            styles["colors__hero-available-colors-list-item-button--dual-tone"],
          isActive &&
            styles["colors__hero-available-colors-list-item-button--active"]
        )}
        style={{
          background: getMultiToneGradientColor(colorCodes),
        }}
        aria-label={`${isActive ? "Selected" : "Select"} ${color.name} colour`}
        aria-current={isActive ? "page" : undefined}
        onClick={(e) => clickHandler(e)}
      >
        <ImageAtom
          src={`${assetLoader()}/images/color-effect-bg.png`}
          alt="color-effect-bg"
          fill
        />
        {isActive && (
          <div className={styles.checkTick}>
            <span className={styles.checkMark}>✓</span>
          </div>
        )}
      </a>
    </>
  );
}
